// The per-plan-type benefits editor (BENEFIT_FIELD_TEMPLATES) backed by
// plans.rawBenefits. Used by the Plan Builder AND the plan detail Benefits
// tab so every surface edits and shows the same field set — what a
// superadmin types in the library is what a broker sees on the client plan.
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getBenefitFieldTemplate } from "@shared/constants/benefitFieldTemplates";

export interface RawBenefitLine {
  name: string;
  value: string;
  network?: "in" | "out";
}

export function TemplatedBenefitsEditor({
  planType,
  networkType,
  value,
  onChange,
}: {
  planType: string | null | undefined;
  /** Medical plans: "HMO" hides the out-of-network column. */
  networkType?: string | null;
  value: RawBenefitLine[];
  onChange: (next: RawBenefitLine[]) => void;
}) {
  const template = getBenefitFieldTemplate(planType);
  if (!template) return null;
  const showOutNetwork =
    template.networkColumns === "both" ||
    (template.networkColumns === "medical" && networkType !== "HMO");

  const rawValue = (name: string, network: "in" | "out") =>
    value.find((r) => r.name === name && (r.network ?? "in") === network)?.value ?? "";
  const setRawValue = (name: string, network: "in" | "out", next: string) => {
    const idx = value.findIndex((r) => r.name === name && (r.network ?? "in") === network);
    const out = [...value];
    // Keep the raw keystrokes — trimming here ate spaces AS THE USER TYPED
    // ("No Charge" became "NoCharge"). Whitespace-only clears the entry;
    // final trimming happens on submit.
    if (!next.trim()) {
      if (idx >= 0) out.splice(idx, 1);
      onChange(out);
      return;
    }
    if (idx >= 0) out[idx] = { ...out[idx], value: next };
    else out.push({ name, value: next, network });
    onChange(out);
  };
  // "split" fields store "<individual> / <family>" in one W&B line.
  const splitParts = (v: string): [string, string] => {
    const [a, b] = v.split("/");
    return [(a ?? "").trim(), (b ?? "").trim()];
  };
  const joinParts = (ind: string, fam: string) =>
    ind.trim() || fam.trim() ? `${ind.trim()} / ${fam.trim()}` : "";

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Standard carrier benefit fields. Values are free text (e.g. "$20 Copay",
        "No Charge", "40% after deductible").
        {template.networkColumns === "medical" && !showOutNetwork ? " HMO — in-network only." : ""}
      </p>
      {template.groups.map((grp) => (
        <div key={grp.group} className="space-y-1.5">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {grp.group}
          </h4>
          {grp.fields.map((f) => {
            const [inInd, inFam] = splitParts(rawValue(f.name, "in"));
            const [outInd, outFam] = splitParts(rawValue(f.name, "out"));
            return (
              <div key={f.name} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                <Label className="text-sm font-normal">{f.label}</Label>
                <div className="flex items-center gap-2">
                  {f.split ? (
                    <>
                      <Input className="h-8 w-24 text-sm" placeholder="Individual" value={inInd}
                        onChange={(e) => setRawValue(f.name, "in", joinParts(e.target.value, inFam))} />
                      <Input className="h-8 w-24 text-sm" placeholder="Family" value={inFam}
                        onChange={(e) => setRawValue(f.name, "in", joinParts(inInd, e.target.value))} />
                    </>
                  ) : (
                    <Input className="h-8 w-44 text-sm"
                      placeholder={template.networkColumns === "none" ? "Value" : "In-network"}
                      value={rawValue(f.name, "in")}
                      onChange={(e) => setRawValue(f.name, "in", e.target.value)} />
                  )}
                  {showOutNetwork &&
                    (f.split ? (
                      <>
                        <Input className="h-8 w-24 text-sm" placeholder="Out · Ind" value={outInd}
                          onChange={(e) => setRawValue(f.name, "out", joinParts(e.target.value, outFam))} />
                        <Input className="h-8 w-24 text-sm" placeholder="Out · Fam" value={outFam}
                          onChange={(e) => setRawValue(f.name, "out", joinParts(outInd, e.target.value))} />
                      </>
                    ) : (
                      <Input className="h-8 w-44 text-sm" placeholder="Out-of-network" value={rawValue(f.name, "out")}
                        onChange={(e) => setRawValue(f.name, "out", e.target.value)} />
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** Submit-time normalisation shared by every saver: trim, drop blanks. */
export function normalizeRawBenefits(lines: RawBenefitLine[]): RawBenefitLine[] {
  return lines.map((r) => ({ ...r, value: String(r.value ?? "").trim() })).filter((r) => r.value);
}
